"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.OnboardingToast = exports.TOAST_MS = void 0;
const util_1 = require("@app/util");
const _ui_1 = require("@ui");
const classnames_1 = __importDefault(require("classnames"));
const react_1 = __importDefault(require("react"));
const onboarding_confetti_1 = require("./onboarding-confetti");
require("./onboarding-toast.scss");
/**
 * How long the toast stays up before it closes itself. Also drives the bar across the top,
 * which is why it is passed to the animation rather than duplicated in SCSS — the bar has to
 * run out at the exact moment `onExpire` fires, or it would be lying about how much time is
 * left.
 */
exports.TOAST_MS = 5000;
/** Long enough that the pips are seen in their „active" state before they start moving. */
const FLIP_DELAY_MS = 700;
/** when several steps were finished at once, they widen one after another, left to right */
const FLIP_STAGGER_MS = 250;
/**
 * Shown once a step of the onboarding checklist is detected as finished (see
 * `use-onboarding-step-toast`). It is deliberately a small replay of the checklist's hero: the
 * same row of pips, with the pip of the step just finished animating from active to done a
 * beat after the toast appears, so the manager sees the progress they just made rather than
 * only being told about it.
 */
const OnboardingToast = ({ pips, completedIds, onContinue, onExpire, }) => {
    const [isFlipped, setIsFlipped] = react_1.default.useState(false);
    react_1.default.useEffect(() => {
        const flip = window.setTimeout(() => setIsFlipped(true), FLIP_DELAY_MS);
        const expiry = window.setTimeout(onExpire ?? onContinue, exports.TOAST_MS);
        return () => {
            window.clearTimeout(flip);
            window.clearTimeout(expiry);
        };
    }, []);
    // the freshly finished steps are held back until the flip, so that the pip row starts out
    // looking exactly as the checklist left it and only then catches up
    const isPipDone = (pip) => pip.isDone && (!completedIds.includes(pip.id) || isFlipped);
    const activeId = pips.find((pip) => !isPipDone(pip))?.id;
    // ordered by position in the row, not by the order the steps were finished in, so the
    // widening always runs left to right. A pip that is not in it comes back as -1 and is
    // clamped to no delay at all.
    const flipOrder = pips.filter((pip) => completedIds.includes(pip.id)).map((pip) => pip.id);
    const flipDelayMs = (pip) => Math.max(0, flipOrder.indexOf(pip.id)) * FLIP_STAGGER_MS;
    return (react_1.default.createElement("div", { className: 'onboarding-toast', "data-testid": 'onboarding-toast' },
        react_1.default.createElement(onboarding_confetti_1.OnboardingConfetti, null),
        react_1.default.createElement("div", { className: 'onboarding-toast-timer' },
            react_1.default.createElement("div", { className: 'onboarding-toast-timer-bar', style: { animationDuration: `${exports.TOAST_MS}ms` } })),
        react_1.default.createElement("div", { className: 'onboarding-toast-body' },
            react_1.default.createElement("span", { className: 'onboarding-toast-title', "data-testid": 'onboarding-toast-title' }, (0, util_1.t)('ONB_TOAST_STEP_DONE')),
            react_1.default.createElement("span", { className: 'onboarding-toast-desc' }, (0, util_1.t)('ONB_TOAST_STEP_DONE_DESC')),
            react_1.default.createElement("div", { className: 'onboarding-toast-pips' }, pips.map((pip) => (react_1.default.createElement("span", { key: pip.id, className: (0, classnames_1.default)('onboarding-toast-pip', {
                    done: isPipDone(pip),
                    active: pip.id === activeId,
                    reward: pip.isReward,
                }), style: { transitionDelay: `${flipDelayMs(pip)}ms` }, "data-testid": `onboarding-toast-pip-${pip.id}` })))),
            react_1.default.createElement(_ui_1.Button, { label: (0, util_1.t)('MA_FORM_CONTINUE'), size: _ui_1.Button.SIZES.MEDIUM, style: _ui_1.Button.STYLES.CONTAINED, color: _ui_1.Button.COLORS.DARK_BLUE, onClick: onContinue, testid: 'onboarding-toast-continue' }))));
};
exports.OnboardingToast = OnboardingToast;
exports.default = exports.OnboardingToast;
